import type { ReactNode } from 'react';

import { ArrowLeft, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

interface LegalLayoutProps {
  title: string;
  updatedAt: string;
  /** Mostra o aviso de versão preliminar. Termos de Uso vigentes passam `false` (26/10/2026). */
  preliminary?: boolean;
  children: ReactNode;
}

/**
 * Layout das páginas legais públicas (Termos, Política). Standalone — fora do
 * Layout autenticado, acessível sem login (linkado do checkout público).
 * Enquanto o texto não passa por revisão jurídica, exibe banner de versão
 * preliminar (R1c do registro de riscos do checkout).
 *
 * VISUAL: paleta do checkout público (creme/preto — ver
 * components/checkout-public/kit.tsx), NUNCA os tokens do painel admin: o
 * cliente clica em "Termos de Uso" no rodapé do checkout claro e não pode
 * cair numa página escura de outro produto. Sem o chrome do checkout (selo
 * "pagamento seguro", brushstroke) de propósito — isto é documento, não venda.
 */
export function LegalLayout({
  title,
  updatedAt,
  preliminary = true,
  children,
}: LegalLayoutProps): JSX.Element {
  return (
    <div className="min-h-dvh bg-[#F5F1EC] antialiased [color-scheme:light]">
      <div className="mx-auto max-w-2xl px-6 py-12 md:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-black/50 transition-colors hover:text-black"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          Voltar
        </Link>

        {preliminary && (
          <div className="mt-6 flex items-start gap-2 rounded-[12px] border border-amber-600/30 bg-amber-50 px-4 py-3 text-[12px] leading-snug text-amber-900">
            <FileText size={15} strokeWidth={2} className="mt-px shrink-0" />
            <span>
              Versão preliminar, em revisão jurídica — pode mudar antes da publicação definitiva.
            </span>
          </div>
        )}

        <header className="mt-9">
          <h1 className="text-balance text-[30px] font-black leading-[1.1] tracking-[-0.02em] text-black md:text-[38px]">
            {title}
          </h1>
          <p className="mt-3 text-[13px] text-black/50">Última atualização: {updatedAt}</p>
        </header>

        <div className="mt-10 space-y-9">{children}</div>

        <footer className="mt-14 border-t border-black/10 pt-6 text-[12px] leading-relaxed text-black/45">
          Dúvidas sobre este documento? Fale com o suporte.
          {preliminary && ' Este documento será atualizado quando a versão final for publicada.'}
        </footer>
      </div>
    </div>
  );
}

/**
 * Cláusula numerada. A numeração é funcional em texto legal (permite referência
 * cruzada — "conforme cláusula 6"), não scaffolding decorativo.
 */
export function LegalSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3">
      <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-black">{title}</h2>
      <div className="space-y-3 text-[14px] leading-[1.7] text-black/75">{children}</div>
    </section>
  );
}
